import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Brain, RefreshCw, Loader2, ShieldAlert, LockKeyhole, Timer, Zap, Ban, LineChart, Clock } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PanelErrorBoundary from "@/components/PanelErrorBoundary";
import { renderScalar } from "@/utils/renderScalar";

function pct(v, dp = 2) {
  if (v == null || v === "INSUFFICIENT_SAMPLE") return "—";
  return `${v >= 0 ? "+" : ""}${(v * 100).toFixed(dp)}%`;
}
function normConf(c) {
  if (c == null) return null;
  const cf = Number(c);
  if (Number.isNaN(cf)) return null;
  return cf > 1 ? cf / 100 : cf;
}
function confPct(c) {
  const n = normConf(c);
  return n == null ? "—" : `${(n * 100).toFixed(0)}%`;
}
function toneOf(v) {
  if (v == null) return "";
  return v > 0 ? "pnl-up" : v < 0 ? "pnl-down" : "";
}

function Stat({ label, value, tone = "", hint, "data-testid": testId }) {
  return (
    <div className="flex flex-col gap-0.5" data-testid={testId}>
      <div className="label-cap text-[9px]">{label}</div>
      <div className={`mono text-lg font-medium ${tone}`}>{value ?? "—"}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function BreakdownTable({ title, rows, bucketKey }) {
  if (!rows?.length) return null;
  return (
    <section className="grid-cell !p-3" data-testid={`ai-quality-breakdown-${bucketKey}`}>
      <div className="label-cap mb-2">{title}</div>
      <table className="w-full text-[11px] mono">
        <thead>
          <tr className="text-muted-foreground border-b border-border/40">
            <th className="text-left px-2 py-1">{bucketKey.replace(/_/g, " ")}</th>
            <th className="text-right px-2 py-1">N</th>
            <th className="text-right px-2 py-1">WIN %</th>
            <th className="text-right px-2 py-1">AVG RETURN</th>
            <th className="text-right px-2 py-1">EXPECTANCY</th>
            <th className="text-right px-2 py-1">PROFIT FACTOR</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[bucketKey]} className="border-b border-border/30 last:border-0">
              <td className="px-2 py-1">{r[bucketKey]}</td>
              <td className="text-right px-2 py-1">{r.count}</td>
              <td className="text-right px-2 py-1">{r.win_rate != null ? `${(r.win_rate * 100).toFixed(0)}%` : "—"}</td>
              <td className={`text-right px-2 py-1 ${toneOf(r.average_return)}`}>{pct(r.average_return)}</td>
              <td className={`text-right px-2 py-1 ${toneOf(r.expectancy)}`}>{pct(r.expectancy, 3)}</td>
              <td className="text-right px-2 py-1">{r.profit_factor != null ? Number(r.profit_factor).toFixed(2) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "funnel", label: "Funnel" },
  { id: "policylab", label: "Policy Lab" },
  { id: "diagnosis", label: "Deployment Diagnosis" },
  { id: "shadow", label: "Shadow Portfolio" },
  { id: "horizons", label: "Per-Horizon Cohorts" },
  { id: "quality", label: "Recommendation Quality" },
  { id: "missed", label: "Missed Opportunities" },
  { id: "rejections", label: "Rejections / Opportunity Cost" },
  { id: "frequency", label: "Frequency" },
  { id: "confidence", label: "Confidence" },
  { id: "critic", label: "Analyst vs Critic" },
  { id: "stability", label: "Evidence Stability" },
  { id: "compare", label: "V1 vs V2" },
  { id: "verdicts", label: "Verdict Contract" },
  { id: "runs", label: "Scan Runs" },
  { id: "autonomy", label: "Autonomy Readiness" },
];

const HORIZON_STATUS_TONE = {
  MATURE: "text-signal-green",
  DEVELOPING: "text-[#0A84FF]",
  EARLY_EVIDENCE: "text-signal-yellow",
  INSUFFICIENT_SAMPLE: "text-muted-foreground",
  NO_DATA: "text-muted-foreground",
};


function SchedulerVerifyButton({ onDone }) {
  const [state, setState] = useState({ mode: "initializing" });
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── Rehydrate from backend on mount so a browser refresh does NOT
  //   silently drop an in-flight verification (P2 fix).  Backend is
  //   the single source of truth; the button state is derived, never
  //   invented client-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/ai-quality/scheduler-verify/status");
        const v = r.data || {};
        if (cancelled) return;
        const s = (v.status || "").toUpperCase();
        if (s === "WAITING" || s === "RUNNING") {
          setState({ mode: "polling", verify_id: v.verify_id, verify: v });
        } else {
          setState({ mode: "idle" });
        }
      } catch (e) {
        if (!cancelled) setState({ mode: "idle" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (state.mode !== "polling") return;
    const iv = setInterval(async () => {
      try {
        const r = await api.get("/ai-quality/scheduler-verify/status",
                                 { params: { verify_id: state.verify_id }});
        const v = r.data;
        if (v?.status === "PASS" || v?.status === "FAIL") {
          setState({ mode: "done", verify: v });
          clearInterval(iv);
          onDone && onDone();
        } else setState((s) => ({ ...s, verify: v }));
      } catch (e) { /* poll ignored */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [state.mode, state.verify_id, onDone]);

  const submitVerification = async () => {
    setConfirmOpen(false);
    setState({ mode: "requesting" });
    try {
      const r = await api.post("/ai-quality/scheduler-verify",
                                null, { params: { offset_minutes: 3 }});
      // Backend guard: if a verification is already in-flight, honour
      // it instead of queueing a duplicate. See P3.
      if (r.data?.status === "VERIFICATION_ALREADY_ACTIVE") {
        const existing = r.data.existing || {};
        toast.info("A verification is already active — showing its status.");
        setState({ mode: "polling",
                   verify_id: existing.verify_id, verify: existing });
        return;
      }
      toast.success(`Verification queued for ${new Date(r.data.target_slot_et).toLocaleTimeString("en-US",{timeZone:"America/New_York",hour:"2-digit",minute:"2-digit",hour12:true})} ET`);
      setState({ mode: "polling", verify_id: r.data.verify_id,
                  verify: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to schedule verification");
      setState({ mode: "idle" });
    }
  };

  const confirmDialog = (
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent
        className="bg-background border-border font-mono"
        data-testid="scheduler-verify-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-signal-green tracking-wide">
            SCHEDULE VERIFICATION RUN
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-xs text-muted-foreground space-y-3 pt-2">
              <p>
                Schedules a <span className="text-foreground">one-time</span> research
                verification <span className="text-foreground">~2–3 min from now</span>
                {" "}using the production scheduler path.
              </p>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
                <span className="text-muted-foreground">Est. AI cost</span>
                <span className="text-foreground">~$0.10 – $0.30</span>
                <span className="text-muted-foreground">Counts as PREMARKET / MIDDAY</span>
                <span className="text-signal-yellow">NO</span>
                <span className="text-muted-foreground">Counts as independent evidence</span>
                <span className="text-signal-yellow">NO</span>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="scheduler-verify-cancel-btn"
            className="font-mono text-xs">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={submitVerification}
            data-testid="scheduler-verify-confirm-btn"
            className="font-mono text-xs bg-signal-green text-background hover:bg-signal-green/90">
            Schedule Run
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (state.mode === "initializing") {
    // Backend rehydration in-flight — do NOT let the operator queue a
    // fresh verification until we know whether one is already active.
    return (
      <button disabled className="chip !text-[9px] opacity-60"
              data-testid="scheduler-verify-initializing">
        <Loader2 size={10} className="inline animate-spin mr-1"/>
        LOADING…
      </button>
    );
  }
  if (state.mode === "idle") {
    return (
      <>
        <button onClick={() => setConfirmOpen(true)} className="chip !text-[9px] hover:border-foreground"
                data-testid="run-scheduler-verify-btn">RUN SCHEDULER VERIFICATION</button>
        {confirmDialog}
      </>
    );
  }
  const v = state.verify;
  const targetET = v?.target_slot_et
    ? new Date(v.target_slot_et).toLocaleTimeString("en-US",
        {timeZone:"America/New_York",hour:"2-digit",minute:"2-digit",hour12:true})
    : "—";
  const tone = v?.status === "PASS" ? "text-signal-green border-signal-green/40"
             : v?.status === "FAIL" ? "text-signal-yellow border-signal-yellow/40"
             : "text-[#0A84FF] border-[#0A84FF]/40";
  return (
    <div className="flex items-center gap-2 text-[10px] mono" data-testid="verify-status">
      <span className={`chip !text-[9px] ${tone}`}>{v?.status || "REQUESTING"}</span>
      <span className="text-muted-foreground">slot: {targetET} ET</span>
      {v?.scan_run_id && <span className="text-muted-foreground">run: {v.scan_run_id}</span>}
      {state.mode === "done" && (
        <button className="chip !text-[9px]" onClick={() => setState({mode:"idle"})}>
          Dismiss
        </button>
      )}
    </div>
  );
}

export default function AIQuality() {
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [maturity, setMaturity] = useState(null);
  const [shadow, setShadow] = useState(null);
  const [missed, setMissed] = useState(null);
  const [critic, setCritic] = useState(null);
  const [autonomy, setAutonomy] = useState(null);
  const [recs, setRecs] = useState([]);
  const [horizons, setHorizons] = useState(null);
  const [frequency, setFrequency] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [stability, setStability] = useState(null);
  const [rejections, setRejections] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [policyLab, setPolicyLab] = useState(null);
  const [idleCash, setIdleCash] = useState(null);
  const [pullback, setPullback] = useState(null);
  const [activeWin, setActiveWin] = useState(null);
  const [verdictContract, setVerdictContract] = useState(null);
  const [scanRuns, setScanRuns] = useState(null);
  const [expandedRun, setExpandedRun] = useState(null);
  const [amPmDate, setAmPmDate] = useState(null);
  const [amPm, setAmPm] = useState(null);
  const [lineage, setLineage] = useState(null);
  const [spyProof, setSpyProof] = useState(null);

  const load = async () => {
    setLoading(true);
    // Phase 1 — critical shell (lightweight endpoints only). If any
    // of these fail we still render the page; individual panels take
    // their own error state.  This eliminates the 8-9 second
    // Promise.all wait that caused Cloudflare origin timeouts on
    // production. See P0/P1/P8.
    try {
      const criticalPairs = await Promise.all([
        ["status", api.get("/ai-quality/status")],
        ["summary", api.get("/ai-quality/summary")],
        ["scan-runs", api.get("/ai-quality/scan-runs?limit=50")],
        ["countdown", api.get("/ai-quality/countdown")],
        ["autonomy-readiness", api.get("/ai-quality/autonomy-readiness")],
        ["evidence-lineage", api.get("/ai-quality/evidence-lineage")],
        ["spy-benchmark-proof", api.get("/ai-quality/spy-benchmark-proof")],
      ].map(async ([k, p]) => {
        try { return [k, (await p).data, null]; }
        catch (e) { return [k, null, e]; }
      }));
      for (const [k, v] of criticalPairs) {
        if (v == null) continue;
        if (k === "status") setStatus(v);
        else if (k === "summary") setData(v);
        else if (k === "scan-runs") setScanRuns(v);
        else if (k === "countdown") setCountdown(v);
        else if (k === "autonomy-readiness") setAutonomy(v);
        else if (k === "evidence-lineage") setLineage(v);
        else if (k === "spy-benchmark-proof") setSpyProof(v);
      }
    } finally { setLoading(false); }

    // Phase 2 — heavier / analytics endpoints load in the background.
    // Each one is isolated: a failure toasts locally but does NOT
    // blank the page.  `shadow-portfolio` (~9s) and `pullback-
    // analysis` (~4.5s) are the two known-slow endpoints — putting
    // them here means the shell paints immediately.
    const bg = [
      ["maturity", setMaturity],
      ["shadow-portfolio", setShadow],
      ["missed-opportunities", setMissed],
      ["analyst-vs-critic", setCritic],
      ["recommendations?limit=200", setRecs],
      ["per-horizon", setHorizons],
      ["frequency", setFrequency],
      ["stability", setStability],
      ["shadow-portfolio/rejections", setRejections],
      ["shadow-portfolio/comparison", setComparison],
      ["funnel", setFunnel],
      ["idle-cash-diagnosis", setIdleCash],
      ["pullback-analysis", setPullback],
      ["active-window", setActiveWin],
      ["verdict-contract", setVerdictContract],
    ];
    await Promise.allSettled(bg.map(async ([path, setter]) => {
      try { setter((await api.get(`/ai-quality/${path}`)).data); }
      catch (e) {
        // Silently record — this is background analytics, do not
        // interrupt the operator. The panel will still show its
        // empty/error state derived from a null value.
      }
    }));

    // Policy Lab is the heaviest calc — fire last, once.
    try { setPolicyLab((await api.get("/ai-quality/policy-lab",
      { params: { cash_yield_annual_pct: 0.0525 }})).data); }
    catch { setPolicyLab({ error: "Policy Lab load failed" }); }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/ai-quality/refresh");
      toast.success(`Snapshots +${r.data.snapshot_backfill.snapshots_created} · outcomes ${r.data.outcome_tracking.updated}`);
      await load();
    } catch (e) { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const s = data?.summary || {};
  const mat = maturity || {};
  const sh = shadow?.summary || {};

  const badgeToneFor = (statusId) => {
    if (statusId === "VALIDATION_READY") return "text-signal-green border-signal-green/40";
    if (statusId === "PARTIALLY_MATURED") return "text-[#0A84FF] border-[#0A84FF]/40";
    if (statusId === "EARLY_EVIDENCE") return "text-signal-yellow border-signal-yellow/40";
    return "text-signal-yellow border-signal-yellow/40";
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="ai-quality-root">
      <div className="flex items-center flex-wrap gap-2">
        <Brain size={16}/>
        <h1 className="text-xl font-medium tracking-tight">AI Validation</h1>
        <span className={`chip !text-[9px] ${badgeToneFor(mat.status)}`}
            data-testid="ai-quality-status-chip">
          {(mat.status || "COLLECTING_EVIDENCE").replace(/_/g, " ")}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={refresh} disabled={refreshing}
              className="btn-outline h-7 !px-3 text-[11px] flex items-center gap-1"
              data-testid="ai-quality-refresh-btn">
            {refreshing ? <Loader2 size={11} className="animate-spin"/> : <RefreshCw size={11}/>}
            Refresh Outcomes
          </button>
        </div>
      </div>

      {/* Evidence Lineage banner — the honest cohort split (P4/P5/P10). */}
      {lineage && (
        <section className="grid-cell !p-3 border-l-2 border-signal-blue"
                 data-testid="evidence-lineage-banner">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="chip !text-[9px] text-signal-blue border-signal-blue/40">EVIDENCE LINEAGE</span>
            <span className="label-cap">Only INDEPENDENT unattended research counts toward AI validation</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-[11px]">
            <Stat label="INDEPENDENT TRACKED"
                  value={lineage.independent_tracked ?? 0}
                  tone={(lineage.independent_tracked ?? 0) > 0 ? "" : "text-signal-yellow"}
                  data-testid="lineage-independent-tracked"/>
            <Stat label="VERIFICATION EXCLUDED"
                  value={lineage.verification_excluded ?? 0}
                  hint="scheduler-verify runs"
                  data-testid="lineage-verification-excluded"/>
            <Stat label="DIAGNOSTIC EXCLUDED"
                  value={lineage.diagnostic_excluded ?? 0}
                  hint="manual / debug runs"
                  data-testid="lineage-diagnostic-excluded"/>
            <Stat label="LINEAGE UNKNOWN"
                  value={lineage.lineage_unknown_excluded ?? 0}
                  tone={(lineage.lineage_unknown_excluded ?? 0) > 0 ? "text-signal-yellow" : ""}
                  hint="historical rows w/o lineage → excluded"
                  data-testid="lineage-unknown-excluded"/>
            <Stat label="TOTAL SNAPSHOTS"
                  value={lineage.total_snapshots ?? 0}/>
          </div>
          {(lineage.independent_tracked ?? 0) === 0 && (
            <div className="mt-2 text-[10px] mono text-signal-yellow leading-relaxed"
                 data-testid="lineage-empty-state">
              No unattended INDEPENDENT research scans have completed yet. Every panel below is filtered to independent evidence only — verification, diagnostic, and pre-lineage snapshots are excluded.
            </div>
          )}
        </section>
      )}

      {/* Maturity strip — the "when will this be trustworthy?" answer. */}
      <section className="grid-cell !p-3" data-testid="ai-quality-maturity">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          <Stat label="TRACKED" value={mat.total_tracked}/>
          <Stat label="T+1 MATURED" value={mat.matured_counts?.T1D}/>
          <Stat label="T+3 MATURED" value={mat.matured_counts?.T3D}/>
          <Stat label="T+5 MATURED" value={mat.matured_counts?.T5D}/>
          <Stat label="T+10 MATURED" value={mat.matured_counts?.T10D}/>
          <Stat label="T+20 MATURED" value={mat.matured_counts?.T20D}
                tone={mat.matured_counts?.T20D === 0 ? "text-signal-yellow" : ""}/>
          <Stat label="FIRST FULL COHORT"
                value={mat.next_maturity_dates?.T20D
                    ? new Date(mat.next_maturity_dates.T20D).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                    : "—"}
                hint="T+20 opens on this date"/>
        </div>
        {mat.data_incomplete > 0 && (
          <div className="mt-2 text-[10px] mono text-signal-yellow">
            ⚠ {mat.data_incomplete} recommendation{mat.data_incomplete === 1 ? "" : "s"} DATA_INCOMPLETE — excluded from all metrics
          </div>
        )}
      </section>

      {/* Early-evidence banner — makes dataset limits impossible to miss. */}
      {activeWin && (
        <section className="grid-cell !p-3 border-l-2 border-signal-yellow" data-testid="early-evidence-banner">
          <div className="flex items-center gap-2 mb-2">
            <span className="chip !text-[9px] text-signal-yellow border-signal-yellow/40">EARLY EVIDENCE</span>
            <span className="label-cap">Portfolio performance comparisons are preliminary</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-[11px]">
            <Stat label="RECOMMENDATIONS" value={activeWin.recommendation_count}/>
            <Stat label="FIRST REC" value={activeWin.first_recommendation_date}/>
            <Stat label="LAST REC" value={activeWin.last_recommendation_date}/>
            <Stat label="LAST MATURED" value={activeWin.last_matured_horizon_date || "—"}/>
            <Stat label="T+20 MATURED" value={mat.matured_counts?.T20D ?? 0} tone={mat.matured_counts?.T20D === 0 ? "text-signal-yellow" : ""}/>
            <Stat label="FIRST T+20 COHORT" value={mat.next_maturity_dates?.T20D || "—"}/>
          </div>
          <div className="mt-3 text-[10px] mono text-muted-foreground leading-relaxed">
            The active research window spans only <span className="text-foreground">{activeWin.first_recommendation_date} → {activeWin.last_recommendation_date}</span> — subsequent trading days in the equity curve had no new recommendations to open. Metrics labelled &quot;Alpha vs SPY&quot; below reflect <span className="text-foreground">FULL-CAPITAL OPPORTUNITY COST</span>, not active-trading underperformance. See the <button onClick={() => setTab("diagnosis")} className="underline decoration-dotted">Deployment Diagnosis</button> tab for four distinct alpha concepts.
          </div>
        </section>
      )}

      {/* Headline card row */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-px bg-border/60" data-testid="ai-quality-headline">
        <div className="grid-cell !p-4">
          <div className="label-cap">Shadow WingWise</div>
          <div className="data-lg mt-2">${sh.current_equity != null ? Number(sh.current_equity).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}) : "—"}</div>
          <div className={`text-[11px] mono mt-1 ${toneOf(sh.total_return_pct)}`}>{pct(sh.total_return_pct)}</div>
        </div>
        <div className="grid-cell !p-4">
          <div className="label-cap">SPY Return</div>
          <div className={`data-lg mt-2 ${toneOf(sh.spy_return_pct)}`}>{pct(sh.spy_return_pct)}</div>
          <div className="text-[11px] mono mt-1 text-muted-foreground"
               data-testid="spy-benchmark-label"
               title={spyProof?.truthful_label || ""}>
            {spyProof?.truthful_label
              ? `${spyProof.trading_days}-day replay baseline`
              : "Replay baseline"}
          </div>
        </div>
        <div className="grid-cell !p-4">
          <div className="label-cap">Alpha vs SPY</div>
          <div className={`data-lg mt-2 ${toneOf(sh.alpha_pct)}`}>{pct(sh.alpha_pct)}</div>
          <div className="text-[11px] mono mt-1 text-muted-foreground">{sh.trade_count ?? 0} trades</div>
        </div>
        <div className="grid-cell !p-4">
          <div className="label-cap">Max Drawdown</div>
          <div className={`data-lg mt-2 ${toneOf(sh.max_drawdown_pct)}`}>{pct(sh.max_drawdown_pct)}</div>
          <div className="text-[11px] mono mt-1 text-muted-foreground">PF {sh.profit_factor != null ? Number(sh.profit_factor).toFixed(2) : "—"}</div>
        </div>
      </section>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border/60" data-testid="ai-quality-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`px-3 py-1.5 text-[11px] mono uppercase tracking-widest ${tab === t.id ? "text-foreground border-b border-foreground -mb-px" : "text-muted-foreground hover:text-foreground/70"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <section className="grid-cell !p-3">
            <div className="label-cap mb-2">Cohort Status</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <Stat label="TOTAL SNAPSHOTS" value={status?.snapshots_total}/>
              <Stat label="POINT-IN-TIME COMPLETE" value={status?.snapshots_point_in_time_complete}/>
              <Stat label="LEGACY INCOMPLETE" value={status?.snapshots_legacy_incomplete} tone="text-signal-yellow" hint="excluded from metrics"/>
              <Stat label="OUTCOMES TRACKED" value={status?.outcomes_tracked}/>
              <Stat label="MATURED (T+20)" value={status?.outcomes_matured}/>
              <Stat label="ELIGIBLE FOR METRICS" value={status?.eligible_for_metrics}/>
            </div>
          </section>
        </>
      )}

      {tab === "funnel" && funnel && (
        <section className="grid-cell !p-3" data-testid="funnel-panel">
          <div className="label-cap mb-2">Recommendation Funnel · what happens to every recommendation</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] mono">
              <thead>
                <tr className="text-muted-foreground border-b border-border/40">
                  <th className="text-left px-2 py-1">STAGE</th>
                  <th className="text-right px-2 py-1">COUNT</th>
                  <th className="text-right px-2 py-1">% OF PREVIOUS</th>
                  <th className="text-right px-2 py-1">% OF TOTAL</th>
                  <th className="text-left px-2 py-1">DETAIL</th>
                </tr>
              </thead>
              <tbody>
                {funnel.stages.map(s => (
                  <tr key={s.stage} className="border-b border-border/30 last:border-0 align-top">
                    <td className="px-2 py-1 font-medium">{s.stage.replace(/_/g," ")}</td>
                    <td className="text-right px-2 py-1">{s.count ?? "—"}</td>
                    <td className="text-right px-2 py-1">{s.pct_of_previous != null ? `${(s.pct_of_previous*100).toFixed(1)}%` : "—"}</td>
                    <td className="text-right px-2 py-1">{s.pct_of_total != null ? `${(s.pct_of_total*100).toFixed(1)}%` : "—"}</td>
                    <td className="px-2 py-1 text-muted-foreground text-[10px]">
                      {s.detail && Object.entries(s.detail).slice(0, 6).map(([k, v]) => (
                        <div key={k}><span className="opacity-60">{k}:</span> {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[10px] text-muted-foreground">
            Verdict counts: {Object.entries(funnel.verdict_counts || {}).map(([k,v]) => `${k}=${v}`).join(" · ")}<br/>
            Rejections: {Object.entries(funnel.rejection_counts_by_reason || {}).map(([k,v]) => `${k}=${v}`).join(" · ") || "—"}
          </div>
          {funnel.reconciliation && (
            <div className="mt-3 pt-3 border-t border-border/40 text-[10px] mono"
                 data-testid="funnel-reconciliation">
              <div className="label-cap mb-1">Reconciliation · {funnel.reconciliation.invariant}</div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <Stat label="ELIGIBLE FOR SHADOW" value={funnel.reconciliation.eligible_for_shadow ?? funnel.reconciliation.capital_available}
                      data-testid="funnel-eligible"/>
                <Stat label="OPENED" value={funnel.reconciliation.opened ?? funnel.reconciliation.trades_opened}
                      data-testid="funnel-opened"/>
                <Stat label="REJECTED W/ REASON" value={funnel.reconciliation.rejected_with_reason ?? 0}
                      hint="each from a persisted rejection reason"
                      data-testid="funnel-rejected-with-reason"/>
                <Stat label="PENDING PULLBACK SETUP" value={funnel.reconciliation.pending_pullback_setup ?? 0}
                      hint="conditional entry still valid but not yet triggered in available evidence"
                      data-testid="funnel-pending-pullback-setup"/>
                <Stat label="PENDING NEXT SESSION" value={funnel.reconciliation.pending_next_market_session}
                      hint="deferred to next SPY bar"
                      data-testid="funnel-pending-next-session"/>
                <Stat label="NOT YET SIMULATED" value={funnel.reconciliation.not_yet_simulated ?? 0}
                      hint="no completed replay run covers these"
                      data-testid="funnel-not-yet-simulated"/>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-2">
                <Stat label="MISMATCH" value={funnel.reconciliation.reconciliation_mismatch ?? 0}
                      hint="unexplained residual after a completed replay"
                      data-testid="funnel-reconciliation-mismatch"/>
              </div>
              {funnel.reconciliation.replay_ever_completed === false && (
                <div className="mt-2 text-signal-yellow" data-testid="funnel-no-replay-warning">
                  ⚠ No completed shadow replay covers this cohort — {funnel.reconciliation.not_yet_simulated ?? 0} eligible recommendations are NOT rejected, they are awaiting simulation.
                  {funnel.reconciliation.latest_failed_replay && (
                    <> Last replay FAILED: {funnel.reconciliation.latest_failed_replay.failure_reason}.</>
                  )}
                </div>
              )}
              {(funnel.reconciliation.reconciliation_mismatch ?? 0) > 0 && (
                <div className="mt-2 text-signal-red" data-testid="funnel-mismatch-warning">
                  ⚠ {funnel.reconciliation.reconciliation_mismatch} eligible recommendation(s) lack an explainable terminal state after a completed replay — investigate.
                </div>
              )}
              {(funnel.reconciliation.diagnostic_trades_preserved ?? 0) > 0 && (
                <div className="mt-2 text-signal-yellow">
                  ⚠ {funnel.reconciliation.diagnostic_trades_preserved} diagnostic trades (verification-derived) preserved but excluded from INDEPENDENT invariant.
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {tab === "policylab" && (
        <section className="grid-cell !p-3" data-testid="policy-lab-panel">
          <div className="label-cap mb-2 flex items-center gap-2">
            <LineChart size={12}/> Policy Sensitivity Lab · dry-run comparison of five sizing policies
          </div>
          {!policyLab ? (
            <div className="text-[11px] text-muted-foreground flex items-center gap-2">
              <Loader2 size={12} className="animate-spin"/> Running Policies A-E against the immutable cohort…
            </div>
          ) : policyLab.error ? (
            <div className="text-[11px] pnl-down">{policyLab.error}</div>
          ) : (
            <>
              <div className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                Only the sizing basis / percent varies across policies. All other rules
                (entry, exit, slippage, spread, fees, verdict eligibility, sector data,
                ambiguous-bar handling) are held constant. Cash yield assumption:
                <span className="text-foreground ml-1">{(policyLab.cash_yield_annual_pct*100).toFixed(2)}% annual</span> —
                configurable via the <span className="text-foreground">cash_yield_annual_pct</span> query parameter.
                <br/>Policies were declared BEFORE examining results — no parameter search,
                no cherry-picking. Sensitivity analysis only. Production risk config is unchanged.
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] mono">
                  <thead>
                    <tr className="text-muted-foreground border-b border-border/40">
                      <th className="text-left px-2 py-1">POLICY</th>
                      <th className="text-right px-2 py-1">RETURN</th>
                      <th className="text-right px-2 py-1">α vs SPY (full)</th>
                      <th className="text-right px-2 py-1">DM SPY (0%)</th>
                      <th className="text-right px-2 py-1">DM SPY (yield)</th>
                      <th className="text-right px-2 py-1">α DM SPY 0%</th>
                      <th className="text-right px-2 py-1">DM QQQ (0%)</th>
                      <th className="text-right px-2 py-1">α DM QQQ 0%</th>
                      <th className="text-right px-2 py-1">RODC net</th>
                      <th className="text-right px-2 py-1">DEPLOYED</th>
                      <th className="text-right px-2 py-1">PEAK</th>
                      <th className="text-right px-2 py-1">MAX DD</th>
                      <th className="text-right px-2 py-1">PF</th>
                      <th className="text-right px-2 py-1">EXP</th>
                      <th className="text-right px-2 py-1">TRADES</th>
                      <th className="text-right px-2 py-1">CONC</th>
                      <th className="text-right px-2 py-1">REJECT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {policyLab.policies.map(p => {
                      const s = p.summary; const meta = p.policy;
                      return (
                        <tr key={meta.name} className="border-b border-border/30 last:border-0">
                          <td className="px-2 py-1 font-medium" title={meta.description}>{meta.name}</td>
                          <td className={`text-right px-2 py-1 ${toneOf(s.total_return_pct)}`}>{pct(s.total_return_pct, 3)}</td>
                          <td className={`text-right px-2 py-1 ${toneOf(s.alpha_pct)}`}>{pct(s.alpha_pct, 3)}</td>
                          <td className="text-right px-2 py-1">{pct(s.deployment_matched_spy_zero_cash_pct, 3)}</td>
                          <td className="text-right px-2 py-1">{pct(s.deployment_matched_spy_with_yield_pct, 3)}</td>
                          <td className={`text-right px-2 py-1 ${toneOf(s.alpha_vs_dm_spy_zero_pct)}`}>{pct(s.alpha_vs_dm_spy_zero_pct, 3)}</td>
                          <td className="text-right px-2 py-1">{pct(s.deployment_matched_qqq_zero_cash_pct, 3)}</td>
                          <td className={`text-right px-2 py-1 ${toneOf(s.alpha_vs_dm_qqq_zero_pct)}`}>{pct(s.alpha_vs_dm_qqq_zero_pct, 3)}</td>
                          <td className={`text-right px-2 py-1 ${toneOf(s.rodc_net)}`}>{pct(s.rodc_net, 2)}</td>
                          <td className="text-right px-2 py-1">{pct(s.avg_capital_deployed_pct, 2)}</td>
                          <td className="text-right px-2 py-1">{pct(s.peak_capital_deployed_pct, 1)}</td>
                          <td className={`text-right px-2 py-1 ${toneOf(s.max_drawdown_pct)}`}>{pct(s.max_drawdown_pct, 3)}</td>
                          <td className="text-right px-2 py-1">{s.profit_factor != null ? Number(s.profit_factor).toFixed(2) : "—"}</td>
                          <td className={`text-right px-2 py-1 ${toneOf(s.expectancy_pct)}`}>{pct(s.expectancy_pct, 3)}</td>
                          <td className="text-right px-2 py-1">{s.trade_count}</td>
                          <td className="text-right px-2 py-1">{s.max_concurrent_positions}</td>
                          <td className="text-right px-2 py-1">{s.rejection_total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="grid-cell !p-3">
                  <div className="label-cap mb-1">Four different questions — four different alphas</div>
                  <ul className="text-[10px] text-muted-foreground space-y-1 leading-relaxed">
                    <li><span className="text-foreground">Full-capital opportunity cost</span> — &quot;what if I had invested the whole account in SPY instead?&quot; (`α vs SPY (full)`)</li>
                    <li><span className="text-foreground">Deployment-matched α</span> — &quot;given how much WingWise chose to expose, was the stock selection better than SPY?&quot; (`α DM SPY 0%`)</li>
                    <li><span className="text-foreground">Return on deployed capital</span> — &quot;how productive was the capital actually put at risk?&quot; (`RODC net`)</li>
                    <li><span className="text-foreground">Capital utilization</span> — &quot;did the strategy deploy enough capital to matter?&quot; (`DEPLOYED %`)</li>
                  </ul>
                </div>
                <div className="grid-cell !p-3">
                  <div className="label-cap mb-1">Policy definitions (frozen before results)</div>
                  <ul className="text-[10px] text-muted-foreground space-y-1">
                    {policyLab.policies.map(p => (
                      <li key={p.policy.name}>
                        <span className="text-foreground">{p.policy.name}</span> — {p.policy.description}
                        <div className="opacity-60 ml-3">policy_version: {p.policy.policy_version} · {p.policy.canonical ? "PERSISTED (canonical V2)" : "dry-run"}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {tab === "diagnosis" && (
        <section className="grid-cell !p-3" data-testid="diagnosis-panel">
          <div className="label-cap mb-2 flex items-center gap-2">
            <Timer size={12}/> Deployment Diagnosis · why is Shadow so idle?
          </div>
          {sh.simulation_policy && (
            <div className="mb-4 text-[11px] mono grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="ACCOUNT EQUITY" value={`$${(sh.starting_capital||0).toLocaleString()}`}/>
              <Stat label="POSITION_SIZE_BASIS"
                    value={sh.simulation_metadata?.policy_params?.position_size_basis || "bucket_equity"}
                    tone={(sh.simulation_metadata?.policy_params?.position_size_basis || "bucket_equity") === "bucket_equity" ? "text-signal-yellow" : ""}
                    hint="5% × bucket, NOT × total"/>
              <Stat label="EFFECTIVE ACCT SIZE"
                    value={`≈ ${((sh.simulation_metadata?.policy_params?.position_size_pct?.SWING || 0.05) * (sh.simulation_metadata?.policy_params?.bucket_allocation_pct?.SWING || 0.35) * 100).toFixed(2)}%`}
                    hint="per SWING trade against total account"/>
              <Stat label="AVG DEPLOYED"
                    value={sh.avg_capital_deployed_pct != null ? `${(sh.avg_capital_deployed_pct*100).toFixed(2)}%` : "—"}
                    tone="text-signal-yellow"/>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="grid-cell !p-3">
              <div className="label-cap mb-1">Idle Cash Diagnosis (Shadow V2)</div>
              <table className="w-full text-[11px] mono">
                <thead>
                  <tr className="text-muted-foreground border-b border-border/40">
                    <th className="text-left px-2 py-1">REASON</th>
                    <th className="text-right px-2 py-1">DAYS</th>
                    <th className="text-right px-2 py-1">$ · DAYS</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(idleCash?.idle_days_by_reason || {}).map(([k,v]) => (
                    <tr key={k} className="border-b border-border/30 last:border-0">
                      <td className="px-2 py-1">{k.replace(/_/g," ")}</td>
                      <td className="text-right px-2 py-1">{v}</td>
                      <td className="text-right px-2 py-1">{Number((idleCash?.idle_dollar_days_by_reason || {})[k] || 0).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 text-[10px] text-muted-foreground">
                Trading days examined: {idleCash?.trading_days_examined} · idle: {idleCash?.idle_days_examined} · total idle $-days: {Number(idleCash?.total_idle_dollar_days || 0).toLocaleString()}
              </div>
            </div>
            <div className="grid-cell !p-3">
              <div className="label-cap mb-1">BUY_ON_PULLBACK Analysis</div>
              <div className="grid grid-cols-2 gap-3 text-[11px]">
                <Stat label="TOTAL PULLBACK RECS" value={pullback?.total_pullback_recommendations}/>
                <Stat label="REACHED ENTRY" value={pullback?.reached_entry}/>
                <Stat label="NOT REACHED" value={pullback?.not_reached}/>
                <Stat label="MATURED AFTER REACH" value={pullback?.matured_after_reaching}/>
                <Stat label="AVG DAYS TO ENTRY" value={pullback?.avg_days_to_entry != null ? Number(pullback.avg_days_to_entry).toFixed(1) : "—"}/>
                <Stat label="AVG REALIZED R" value={pullback?.avg_realized_r_when_reached != null ? Number(pullback.avg_realized_r_when_reached).toFixed(2) : "—"}/>
                <Stat label="AVG MFE (REACHED)" value={pct(pullback?.avg_mfe_when_reached)}/>
                <Stat label="AVG MAE (REACHED)" value={pct(pullback?.avg_mae_when_reached)}/>
              </div>
              <div className="mt-2 text-[10px] text-muted-foreground leading-relaxed">
                {pullback?.note}
              </div>
            </div>
          </div>
        </section>
      )}

      {tab === "verdicts" && verdictContract && (
        <section className="grid-cell !p-3" data-testid="verdict-contract-panel">
          <div className="label-cap mb-2">Verdict Semantic Contract</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] mono">
              <thead>
                <tr className="text-muted-foreground border-b border-border/40">
                  <th className="text-left px-2 py-1">VERDICT</th>
                  <th className="text-left px-2 py-1">DIRECTION</th>
                  <th className="text-left px-2 py-1">ENTRY BEHAVIOR</th>
                  <th className="text-center px-2 py-1">TRADE</th>
                  <th className="text-center px-2 py-1">LONG</th>
                  <th className="text-center px-2 py-1">SHORT</th>
                  <th className="text-left px-2 py-1">SCORING</th>
                </tr>
              </thead>
              <tbody>
                {verdictContract.contract.map(v => (
                  <tr key={v.verdict} className="border-b border-border/30 last:border-0 align-top">
                    <td className="px-2 py-1 font-medium">{v.verdict}</td>
                    <td className="px-2 py-1">{v.directional_expectation}</td>
                    <td className="px-2 py-1">{v.entry_behavior}</td>
                    <td className="px-2 py-1 text-center">{v.trade_eligible ? <span className="text-signal-green">✓</span> : <span className="opacity-40">·</span>}</td>
                    <td className="px-2 py-1 text-center">{v.can_open_long ? <span className="text-signal-green">✓</span> : <span className="opacity-40">·</span>}</td>
                    <td className="px-2 py-1 text-center">{v.can_open_short ? <span className="text-signal-green">✓</span> : <span className="opacity-40">·</span>}</td>
                    <td className="px-2 py-1 text-muted-foreground">{v.scoring_rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[10px] text-muted-foreground leading-relaxed">
            AVOID is a <span className="text-foreground">risk statement</span>, NOT a short signal. Cast-vote short simulation is deferred until the verdict contract has been reviewed by the operator.
          </div>
        </section>
      )}

      {tab === "shadow" && (
        <section className="grid-cell !p-3" data-testid="shadow-portfolio-panel">
          <div className="label-cap mb-2 flex items-center gap-2">
            <LineChart size={12}/> Shadow Portfolio · Simulation Only · policy {shadow?.summary?.simulation_policy?.version || "shadow_v2"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Stat label="STARTING CAPITAL" value={`$${(sh.starting_capital || 0).toLocaleString()}`}/>
            <Stat label="EQUITY" value={`$${(sh.current_equity || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`}/>
            <Stat label="REALIZED P&L" value={`${(sh.realized_pnl ?? 0) >= 0 ? "+" : ""}$${(sh.realized_pnl ?? 0).toFixed(2)}`} tone={toneOf(sh.realized_pnl)}/>
            <Stat label="TOTAL RETURN" value={pct(sh.total_return_pct)} tone={toneOf(sh.total_return_pct)}/>
            <Stat label="SPY RETURN" value={pct(sh.spy_return_pct)}/>
            <Stat label="QQQ RETURN" value={pct(sh.qqq_return_pct)}/>
            <Stat label="ALPHA VS SPY" value={pct(sh.alpha_pct)} tone={toneOf(sh.alpha_pct)} hint="full-capital opportunity cost"/>
            <Stat label="ALPHA VS QQQ" value={pct(sh.qqq_alpha_pct)} tone={toneOf(sh.qqq_alpha_pct)} hint="full-capital opportunity cost"/>
            <Stat label="DM SPY (0% cash)" value={pct(sh.deployment_matched_spy_zero_cash_pct)} hint="deployment-matched benchmark"/>
            <Stat label="DM SPY (5.25% cash)" value={pct(sh.deployment_matched_spy_with_yield_pct)} hint="idle cash earns T-bill"/>
            <Stat label="α vs DM SPY 0%" value={pct(sh.alpha_vs_dm_spy_zero_pct, 3)} tone={toneOf(sh.alpha_vs_dm_spy_zero_pct)} hint="stock-selection alpha"/>
            <Stat label="RODC net" value={pct(sh.rodc_net, 2)} tone={toneOf(sh.rodc_net)} hint="return on deployed capital"/>
            <Stat label="TRADES" value={sh.trade_count}/>
            <Stat label="WIN RATE" value={sh.win_rate != null ? `${(sh.win_rate * 100).toFixed(1)}%` : "—"}/>
            <Stat label="PROFIT FACTOR" value={sh.profit_factor != null ? Number(sh.profit_factor).toFixed(2) : "—"}/>
            <Stat label="EXPECTANCY" value={pct(sh.expectancy_pct, 3)}/>
            <Stat label="MAX DRAWDOWN" value={pct(sh.max_drawdown_pct)} tone="pnl-down"/>
            <Stat label="SHARPE" value={sh.sharpe_ratio === "INSUFFICIENT_SAMPLE" ? "INSUFFICIENT" : (sh.sharpe_ratio ?? "—")} tone="text-signal-yellow" hint={!sh.sample_ok ? "n<30" : ""}/>
            <Stat label="TOTAL FEES" value={sh.total_fees != null ? `$${Number(sh.total_fees).toFixed(2)}` : "—"}/>
            <Stat label="MAX CONCURRENT" value={sh.max_concurrent_positions} hint="Positions"/>
            <Stat label="AVG CAPITAL DEPLOYED" value={sh.avg_capital_deployed_pct != null ? `${(sh.avg_capital_deployed_pct*100).toFixed(1)}%` : "—"}/>
            <Stat label="PEAK CAPITAL DEPLOYED" value={sh.peak_capital_deployed_pct != null ? `${(sh.peak_capital_deployed_pct*100).toFixed(1)}%` : "—"}/>
            <Stat label="AVG IDLE CASH" value={sh.avg_idle_cash_pct != null ? `${(sh.avg_idle_cash_pct*100).toFixed(1)}%` : "—"}/>
            <Stat label="DAYS FULLY INVESTED" value={sh.days_fully_invested}/>
            <Stat label="DAYS MOSTLY IDLE" value={sh.days_mostly_idle}/>
            <Stat label="REJECTED TRADES" value={sh.rejection_total} hint="opportunity cost"/>
            <Stat label="TRADING DAYS" value={sh.trading_days_simulated}/>
          </div>
          <div className="mt-3 text-[10px] mono text-muted-foreground">
            Chronological engine: cash committed on entry, released on exit; concurrent open positions; deterministic same-day ranking (score → confidence → risk → time); ambiguous same-bar stop+target defaults to STOP (conservative). Replay id <span className="text-foreground">{shadow?.replay_id}</span>.
          </div>
          {shadow?.trades?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-[11px] mono">
                <thead>
                  <tr className="text-muted-foreground border-b border-border/40">
                    <th className="text-left px-2 py-1">ENTRY</th>
                    <th className="text-left px-2 py-1">SYMBOL</th>
                    <th className="text-left px-2 py-1">BUCKET</th>
                    <th className="text-right px-2 py-1">QTY</th>
                    <th className="text-right px-2 py-1">ENTRY $</th>
                    <th className="text-right px-2 py-1">EXIT $</th>
                    <th className="text-left px-2 py-1">EXIT</th>
                    <th className="text-right px-2 py-1">P&L</th>
                    <th className="text-right px-2 py-1">RETURN</th>
                  </tr>
                </thead>
                <tbody>
                  {shadow.trades.slice(0, 30).map((t) => (
                    <tr key={t.recommendation_snapshot_id + (t.entry_bar_t||"")} className="border-b border-border/30 last:border-0">
                      <td className="px-2 py-1 text-muted-foreground">{(t.entry_bar_t || "").slice(0,10)}</td>
                      <td className="px-2 py-1 font-medium">{t.symbol}</td>
                      <td className="px-2 py-1">{t.bucket || t.portfolio}</td>
                      <td className="text-right px-2 py-1">{t.quantity}</td>
                      <td className="text-right px-2 py-1">${Number(t.entry_price).toFixed(2)}</td>
                      <td className="text-right px-2 py-1">${Number(t.exit_price).toFixed(2)}</td>
                      <td className="px-2 py-1">
                        <span className="chip !text-[9px]">{t.exit_reason}</span>
                      </td>
                      <td className={`text-right px-2 py-1 ${toneOf(t.realized_pnl)}`}>{(t.realized_pnl ?? 0) >= 0 ? "+" : ""}${Number(t.realized_pnl).toFixed(2)}</td>
                      <td className={`text-right px-2 py-1 ${toneOf(t.realized_return_pct)}`}>{pct(t.realized_return_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === "horizons" && (
        <section className="grid-cell !p-3" data-testid="horizons-panel">
          <div className="label-cap mb-2 flex items-center gap-2">
            <Timer size={12}/> Per-Horizon Cohorts · direction-normalized returns
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] mono">
              <thead>
                <tr className="text-muted-foreground border-b border-border/40">
                  <th className="text-left px-2 py-1">HORIZON</th>
                  <th className="text-right px-2 py-1">N</th>
                  <th className="text-right px-2 py-1">WIN RATE</th>
                  <th className="text-right px-2 py-1">AVG RETURN</th>
                  <th className="text-right px-2 py-1">MEDIAN</th>
                  <th className="text-right px-2 py-1">EXPECTANCY</th>
                  <th className="text-right px-2 py-1">PF</th>
                  <th className="text-right px-2 py-1">SPY ALPHA</th>
                  <th className="text-right px-2 py-1">SECTOR ALPHA</th>
                  <th className="text-right px-2 py-1">AVG MFE</th>
                  <th className="text-right px-2 py-1">AVG MAE</th>
                  <th className="text-left px-2 py-1">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {["T1D","T3D","T5D","T10D","T20D"].map(k => {
                  const h = horizons?.[k] || {};
                  const cd = countdown?.horizons?.[k];
                  const insuff = (h.n || 0) < 10;
                  return (
                    <tr key={k} className="border-b border-border/30 last:border-0">
                      <td className="px-2 py-1 font-medium">{k}</td>
                      <td className="text-right px-2 py-1">{h.n ?? 0}</td>
                      <td className="text-right px-2 py-1">{insuff ? "—" : (h.win_rate != null ? `${(h.win_rate*100).toFixed(0)}%` : "—")}</td>
                      <td className={`text-right px-2 py-1 ${insuff ? "text-muted-foreground" : toneOf(h.avg_return)}`}>{insuff ? "—" : pct(h.avg_return)}</td>
                      <td className={`text-right px-2 py-1 ${insuff ? "text-muted-foreground" : toneOf(h.median_return)}`}>{insuff ? "—" : pct(h.median_return)}</td>
                      <td className={`text-right px-2 py-1 ${insuff ? "text-muted-foreground" : toneOf(h.expectancy)}`}>{insuff ? "—" : pct(h.expectancy, 3)}</td>
                      <td className="text-right px-2 py-1">{insuff ? "—" : (h.profit_factor != null ? Number(h.profit_factor).toFixed(2) : "—")}</td>
                      <td className={`text-right px-2 py-1 ${insuff ? "text-muted-foreground" : toneOf(h.spy_alpha_avg)}`}>{insuff ? "—" : pct(h.spy_alpha_avg)}</td>
                      <td className={`text-right px-2 py-1 ${insuff ? "text-muted-foreground" : toneOf(h.sector_alpha_avg)}`}>{insuff ? "—" : pct(h.sector_alpha_avg)}</td>
                      <td className={`text-right px-2 py-1 ${insuff ? "text-muted-foreground" : ""}`}>{insuff ? "—" : pct(h.avg_mfe)}</td>
                      <td className={`text-right px-2 py-1 ${insuff ? "text-muted-foreground" : ""}`}>{insuff ? "—" : pct(h.avg_mae)}</td>
                      <td className={`px-2 py-1 ${HORIZON_STATUS_TONE[h.status] || ""}`}>
                        {(h.status || "NO_DATA").replace(/_/g," ")}
                        {cd?.sessions_remaining != null && cd.sessions_remaining > 0 && (
                          <span className="ml-2 text-muted-foreground">next {cd.sessions_remaining}s · {cd.next_maturity_date}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[10px] text-muted-foreground">
            Sample-size gating: rows below n=10 render as INSUFFICIENT SAMPLE and never display misleading 0% values. Session countdown uses SPY trading calendar with US holiday awareness.
          </div>
        </section>
      )}

      {tab === "rejections" && (
        <section className="grid-cell !p-3" data-testid="rejections-panel">
          <div className="label-cap mb-2 flex items-center gap-2">
            <Ban size={12}/> Rejections / Opportunity Cost · what portfolio rules blocked
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
            <Stat label="TOTAL REJECTIONS" value={rejections?.aggregate?.total_rejections}/>
            <Stat label="MATURED REJECTIONS" value={rejections?.aggregate?.matured_rejections}/>
            <Stat label="MISSED WINNERS" value={rejections?.aggregate?.missed_winners_count}/>
            <Stat label="AVOIDED LOSERS" value={rejections?.aggregate?.avoided_losers_count}/>
            <Stat label="AVG MISSED GAIN" value={pct(rejections?.aggregate?.avg_missed_gain_pct)} tone="pnl-down"/>
            <Stat label="AVG AVOIDED LOSS" value={pct(rejections?.aggregate?.avg_avoided_loss_pct)} tone="pnl-up"/>
          </div>
          <div className="mb-3">
            <div className="label-cap mb-1">By Reason</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(rejections?.counts_by_reason || {}).map(([k,v]) => (
                <div key={k} className="chip !text-[9px]">{k.replace(/_/g," ")} · {v}</div>
              ))}
            </div>
          </div>
          {rejections?.rows?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] mono">
                <thead>
                  <tr className="text-muted-foreground border-b border-border/40">
                    <th className="text-left px-2 py-1">DATE</th>
                    <th className="text-left px-2 py-1">SYMBOL</th>
                    <th className="text-left px-2 py-1">SECTOR</th>
                    <th className="text-left px-2 py-1">VERDICT</th>
                    <th className="text-left px-2 py-1">REASON</th>
                    <th className="text-right px-2 py-1">HYPO T+20</th>
                    <th className="text-right px-2 py-1">HYPO ALPHA</th>
                    <th className="text-center px-2 py-1">MATURED</th>
                  </tr>
                </thead>
                <tbody>
                  {rejections.rows.slice(0, 50).map((r) => (
                    <tr key={r.recommendation_snapshot_id + r.rejection_reason} className="border-b border-border/30 last:border-0">
                      <td className="px-2 py-1 text-muted-foreground">{(r.recommendation_date || "").slice(0,10)}</td>
                      <td className="px-2 py-1 font-medium">{r.symbol}</td>
                      <td className="px-2 py-1">{r.sector || "—"}</td>
                      <td className="px-2 py-1"><span className="chip !text-[9px]">{r.verdict}</span></td>
                      <td className="px-2 py-1 text-signal-yellow">{r.rejection_reason.replace(/_/g," ")}</td>
                      <td className={`px-2 py-1 text-right ${toneOf(r.hypothetical_return_pct)}`}>{r.outcome_matured ? pct(r.hypothetical_return_pct) : "—"}</td>
                      <td className={`px-2 py-1 text-right ${toneOf(r.hypothetical_spy_alpha_pct)}`}>{r.outcome_matured ? pct(r.hypothetical_spy_alpha_pct) : "—"}</td>
                      <td className="px-2 py-1 text-center">{r.outcome_matured ? <span className="text-signal-green">✓</span> : <span className="opacity-40">·</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 text-[10px] text-muted-foreground">
            Hypothetical outcome joined from <span className="text-foreground">recommendation_outcomes</span> AFTER the trade would have matured — never used at decision time. Immature rejected trades render as &quot;—&quot;.
          </div>
        </section>
      )}

      {tab === "frequency" && (
        <section className="grid-cell !p-3" data-testid="frequency-panel">
          <div className="label-cap mb-2 flex items-center gap-2">
            <Zap size={12}/> Recommendation Frequency · selectivity per day
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-3">
            <Stat label="DAYS ACTIVE" value={frequency?.days_active}/>
            <Stat label="TOTAL RECS" value={frequency?.total_recommendations}/>
            <Stat label="AVG / DAY" value={frequency?.avg_per_day}/>
            <Stat label="STRONG_BUY / DAY" value={frequency?.avg_strong_buy_per_day}/>
            <Stat label="BUY / DAY" value={frequency?.avg_buy_per_day}/>
            <Stat label="BUY_ON_PULLBACK / DAY" value={frequency?.avg_buy_on_pullback_per_day}/>
            <Stat label="WATCH / DAY" value={frequency?.avg_watch_per_day}/>
            <Stat label="AVOID / DAY" value={frequency?.avg_avoid_per_day}/>
            <Stat label="SELL / DAY" value={frequency?.avg_sell_per_day}/>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] mono">
              <thead>
                <tr className="text-muted-foreground border-b border-border/40">
                  <th className="text-left px-2 py-1">DATE</th>
                  <th className="text-right px-2 py-1">TOTAL</th>
                  <th className="text-right px-2 py-1">STRONG_BUY</th>
                  <th className="text-right px-2 py-1">BUY</th>
                  <th className="text-right px-2 py-1">WATCH</th>
                  <th className="text-right px-2 py-1">AVOID</th>
                </tr>
              </thead>
              <tbody>
                {(frequency?.days || []).slice(-30).reverse().map((d) => (
                  <tr key={d.date} className="border-b border-border/30 last:border-0">
                    <td className="px-2 py-1 text-muted-foreground">{d.date}</td>
                    <td className="text-right px-2 py-1">{d.total}</td>
                    <td className="text-right px-2 py-1">{d.STRONG_BUY}</td>
                    <td className="text-right px-2 py-1">{d.BUY}</td>
                    <td className="text-right px-2 py-1">{d.WATCH}</td>
                    <td className="text-right px-2 py-1">{d.AVOID}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "stability" && (
        <section className="grid-cell !p-3" data-testid="stability-panel">
          <div className="label-cap mb-2">Evidence Stability · how metrics move as sample grows</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] mono">
              <thead>
                <tr className="text-muted-foreground border-b border-border/40">
                  <th className="text-left px-2 py-1">RECORDED</th>
                  <th className="text-right px-2 py-1">TRADES</th>
                  <th className="text-right px-2 py-1">WIN%</th>
                  <th className="text-right px-2 py-1">EXPECTANCY</th>
                  <th className="text-right px-2 py-1">PF</th>
                  <th className="text-right px-2 py-1">SPY ALPHA</th>
                  <th className="text-right px-2 py-1">QQQ ALPHA</th>
                  <th className="text-right px-2 py-1">MAX DD</th>
                </tr>
              </thead>
              <tbody>
                {(stability?.snapshots || []).slice(-30).reverse().map((s2, i) => (
                  <tr key={s2.replay_id || s2.recorded_at || i} className="border-b border-border/30 last:border-0">
                    <td className="px-2 py-1 text-muted-foreground">{(s2.recorded_at || "").slice(0,10)}</td>
                    <td className="text-right px-2 py-1">{s2.trade_count}</td>
                    <td className="text-right px-2 py-1">{s2.win_rate != null ? `${(s2.win_rate*100).toFixed(0)}%` : "—"}</td>
                    <td className={`text-right px-2 py-1 ${toneOf(s2.expectancy_pct)}`}>{pct(s2.expectancy_pct, 3)}</td>
                    <td className="text-right px-2 py-1">{s2.profit_factor != null ? Number(s2.profit_factor).toFixed(2) : "—"}</td>
                    <td className={`text-right px-2 py-1 ${toneOf(s2.alpha_pct)}`}>{pct(s2.alpha_pct)}</td>
                    <td className={`text-right px-2 py-1 ${toneOf(s2.qqq_alpha_pct)}`}>{pct(s2.qqq_alpha_pct)}</td>
                    <td className={`text-right px-2 py-1 ${toneOf(s2.max_drawdown_pct)}`}>{pct(s2.max_drawdown_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[10px] text-muted-foreground">
            Older snapshots preserved. As sample size grows (n → 30 → 100), metrics should stabilise — swings suggest the current n is too small to trust.
          </div>
        </section>
      )}

      {tab === "compare" && (
        <section className="grid-cell !p-3" data-testid="compare-panel">
          <div className="label-cap mb-2">Shadow V1 (Frictionless) vs V2 (Realistic)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] mono">
              <thead>
                <tr className="text-muted-foreground border-b border-border/40">
                  <th className="text-left px-2 py-1">METRIC</th>
                  <th className="text-right px-2 py-1">V1</th>
                  <th className="text-right px-2 py-1">V2</th>
                  <th className="text-right px-2 py-1">Δ (V2 − V1)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["equity", "current_equity", "$", 2],
                  ["total return", "total_return_pct", "%", 4],
                  ["alpha vs SPY", "alpha_pct", "%", 4],
                  ["alpha vs QQQ", "qqq_alpha_pct", "%", 4],
                  ["win rate", "win_rate", "%", 2],
                  ["profit factor", "profit_factor", "", 2],
                  ["expectancy", "expectancy_pct", "%", 4],
                  ["max drawdown", "max_drawdown_pct", "%", 4],
                  ["trade count", "trade_count", "#", 0],
                  ["max concurrent", "max_concurrent_positions", "#", 0],
                  ["rejections", "rejection_total", "#", 0],
                  ["fees", "total_fees", "$", 2],
                  ["avg deployed", "avg_capital_deployed_pct", "%", 2],
                ].map(([label, key, unit, dp]) => {
                  const v1v = comparison?.v1?.[key]; const v2v = comparison?.v2?.[key];
                  const fmt = (v) => {
                    if (v == null) return "—";
                    if (unit === "%") return `${(v*100).toFixed(dp)}%`;
                    if (unit === "$") return `$${Number(v).toLocaleString(undefined,{minimumFractionDigits:dp, maximumFractionDigits:dp})}`;
                    if (unit === "#") return Number(v).toFixed(0);
                    return Number(v).toFixed(dp);
                  };
                  const delta = (typeof v1v === "number" && typeof v2v === "number") ? v2v - v1v : null;
                  return (
                    <tr key={key} className="border-b border-border/30 last:border-0">
                      <td className="px-2 py-1">{label}</td>
                      <td className="text-right px-2 py-1">{fmt(v1v)}</td>
                      <td className="text-right px-2 py-1">{fmt(v2v)}</td>
                      <td className={`text-right px-2 py-1 ${toneOf(delta)}`}>{fmt(delta)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[10px] text-muted-foreground">
            V1 uses zero slippage / zero fees. V2 layers 2 bps entry + 2 bps exit slippage, 1 bps half-spread, SEC + FINRA TAF on sells. The Δ column shows the real-world cost of executing.
          </div>
        </section>
      )}

      {tab === "quality" && (
        <>
          <section className="grid-cell !p-3">
            <div className="label-cap mb-2">Headline Metrics · T+20 Direction-Normalized</div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Stat label="RECOMMENDATIONS" value={s.total_recommendations}/>
              <Stat label="MATURED" value={s.matured_recommendations}/>
              <Stat label="WIN RATE" value={s.win_rate != null ? `${(s.win_rate * 100).toFixed(1)}%` : "—"}/>
              <Stat label="AVG WINNER" value={pct(s.average_winner)} tone="pnl-up"/>
              <Stat label="AVG LOSER" value={pct(s.average_loser)} tone="pnl-down"/>
              <Stat label="EXPECTANCY" value={pct(s.expectancy, 3)} tone={toneOf(s.expectancy)}/>
              <Stat label="PROFIT FACTOR" value={s.profit_factor != null ? Number(s.profit_factor).toFixed(2) : "—"}/>
              <Stat label="AVG R" value={s.average_r != null ? s.average_r.toFixed(2) : "—"}/>
              <Stat label="MAX DRAWDOWN" value={pct(s.max_drawdown)} tone="pnl-down"/>
              <Stat label="BENCHMARK (SPY)" value={pct(s.benchmark_return_avg)}/>
              <Stat label="ALPHA VS SPY" value={pct(s.alpha_avg)} tone={toneOf(s.alpha_avg)}/>
            </div>
          </section>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <BreakdownTable title="By Verdict"       rows={data?.by_verdict}      bucketKey="verdict"/>
            <BreakdownTable title="By Portfolio"     rows={data?.by_portfolio}    bucketKey="portfolio"/>
            <BreakdownTable title="By Holding Period" rows={data?.by_holding_period} bucketKey="holding_period"/>
            <BreakdownTable title="By Sector"         rows={data?.by_sector}        bucketKey="sector"/>
          </div>
          <section className="grid-cell !p-3">
            <div className="label-cap mb-2">Individual Recommendations · {recs.length} tracked · {recs.filter(r => r.matured).length} matured</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] mono">
                <thead>
                  <tr className="text-muted-foreground border-b border-border/40">
                    <th className="text-left px-2 py-1">DATE</th>
                    <th className="text-left px-2 py-1">SYMBOL</th>
                    <th className="text-left px-2 py-1">VERDICT</th>
                    <th className="text-right px-2 py-1">CONF</th>
                    <th className="text-right px-2 py-1">RISK</th>
                    <th className="text-right px-2 py-1">T+1</th>
                    <th className="text-right px-2 py-1">T+5</th>
                    <th className="text-right px-2 py-1">T+20</th>
                    <th className="text-right px-2 py-1">MFE</th>
                    <th className="text-right px-2 py-1">MAE</th>
                    <th className="text-center px-2 py-1">MATURED</th>
                  </tr>
                </thead>
                <tbody>
                  {recs.slice(0, 50).map((r) => (
                    <tr key={r.id} className="border-b border-border/30 last:border-0" data-testid={`rec-row-${r.symbol}`}>
                      <td className="px-2 py-1 text-muted-foreground">{(r.created_at || "").slice(0,10)}</td>
                      <td className="px-2 py-1 font-medium">{r.symbol}</td>
                      <td className="px-2 py-1"><span className="chip !text-[9px]">{r.verdict}</span></td>
                      <td className="px-2 py-1 text-right">{confPct(r.confidence)}</td>
                      <td className="px-2 py-1 text-right">{r.risk_score != null ? Number(r.risk_score).toFixed(0) : "—"}</td>
                      <td className={`px-2 py-1 text-right ${toneOf(r.returns_pct?.T1D)}`}>{pct(r.returns_pct?.T1D)}</td>
                      <td className={`px-2 py-1 text-right ${toneOf(r.returns_pct?.T5D)}`}>{pct(r.returns_pct?.T5D)}</td>
                      <td className={`px-2 py-1 text-right ${toneOf(r.returns_pct?.T20D)}`}>{pct(r.returns_pct?.T20D)}</td>
                      <td className={`px-2 py-1 text-right ${toneOf(r.mfe_pct)}`}>{pct(r.mfe_pct)}</td>
                      <td className={`px-2 py-1 text-right ${toneOf(r.mae_pct)}`}>{pct(r.mae_pct)}</td>
                      <td className="px-2 py-1 text-center">{r.matured ? <span className="text-signal-green">✓</span> : <span className="opacity-40">·</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {tab === "missed" && (
        <section className="grid-cell !p-3" data-testid="missed-panel">
          <div className="label-cap mb-2">Missed Opportunities · did the Risk Critic block winners?</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
            <Stat label="AVOID / WATCH RECS" value={missed?.avoid_recommendations}/>
            <Stat label="AVOIDED LOSSES (AVG)" value={pct(missed?.avoided_losses_avg)} tone="pnl-up" hint="lower is better outcome"/>
            <Stat label="MISSED GAINS (AVG)" value={pct(missed?.missed_gains_avg)} tone="pnl-down"/>
            {Object.entries(missed?.counts || {}).map(([k, v]) => (
              <Stat key={k} label={k.replace(/_/g, " ")} value={v}/>
            ))}
          </div>
          {missed?.high_confidence_missed_winners?.length > 0 && (
            <div className="mb-3">
              <div className="label-cap text-signal-yellow mb-1 flex items-center gap-1">
                <ShieldAlert size={11}/> High-Confidence Missed Winners
              </div>
              <div className="text-[10px] text-muted-foreground mb-2">
                AVOID / confidence ≥ 80% / T+20 ≥ +5% / clean MAE — the critic may be too conservative here.
              </div>
              <table className="w-full text-[11px] mono">
                <thead>
                  <tr className="text-muted-foreground border-b border-border/40">
                    <th className="text-left px-2 py-1">DATE</th>
                    <th className="text-left px-2 py-1">SYMBOL</th>
                    <th className="text-right px-2 py-1">CONF</th>
                    <th className="text-right px-2 py-1">RISK</th>
                    <th className="text-right px-2 py-1">T+20</th>
                    <th className="text-right px-2 py-1">MFE</th>
                    <th className="text-right px-2 py-1">MAE</th>
                  </tr>
                </thead>
                <tbody>
                  {missed.high_confidence_missed_winners.map((r) => (
                    <tr key={r.snapshot_id}>
                      <td className="px-2 py-1 text-muted-foreground">{(r.recommended_at || "").slice(0,10)}</td>
                      <td className="px-2 py-1 font-medium">{r.symbol}</td>
                      <td className="px-2 py-1 text-right">{confPct(r.confidence)}</td>
                      <td className="px-2 py-1 text-right">{r.risk_score}</td>
                      <td className={`px-2 py-1 text-right ${toneOf(r.returns_pct?.T20D)}`}>{pct(r.returns_pct?.T20D)}</td>
                      <td className={`px-2 py-1 text-right ${toneOf(r.mfe_pct)}`}>{pct(r.mfe_pct)}</td>
                      <td className={`px-2 py-1 text-right ${toneOf(r.mae_pct)}`}>{pct(r.mae_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === "confidence" && (
        <BreakdownTable title="Confidence Calibration · does higher confidence perform better?" rows={data?.confidence_calibration} bucketKey="confidence_bucket"/>
      )}

      {tab === "critic" && (
        <section className="grid-cell !p-3" data-testid="critic-panel">
          <div className="label-cap mb-2">Analyst vs Risk Critic · does the debate add value?</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(critic?.cohorts || {}).map(([k, v]) => (
              <div key={k} className="grid-cell !p-3">
                <div className="label-cap mb-1">{k.replace(/_/g, " ")}</div>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div><span className="label-cap">N</span> <span className="mono">{v.count}</span></div>
                  <div><span className="label-cap">WIN%</span> <span className="mono">{v.win_rate != null ? `${(v.win_rate*100).toFixed(0)}%` : "—"}</span></div>
                  <div><span className="label-cap">AVG RETURN</span> <span className={`mono ${toneOf(v.avg_return)}`}>{pct(v.avg_return)}</span></div>
                  <div><span className="label-cap">EXPECTANCY</span> <span className={`mono ${toneOf(v.expectancy)}`}>{pct(v.expectancy, 3)}</span></div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px]">
            <span className="label-cap">Critic net value per trade:</span>
            <span className={`ml-2 mono ${toneOf(critic?.critic_net_value_per_trade_pct)}`}>
              {pct(critic?.critic_net_value_per_trade_pct, 3)}
            </span>
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground">{critic?.note}</div>
        </section>
      )}

      {tab === "runs" && (
        <section className="grid-cell !p-3" data-testid="scan-runs-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="label-cap flex items-center gap-2">
              <Clock size={12}/> Scan Runs · read-only forensic history
            </div>
            <SchedulerVerifyButton onDone={load}/>
          </div>
          {!scanRuns?.runs?.length ? (
            <div className="text-[11px] text-muted-foreground">No scan runs recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] mono">
                <thead>
                  <tr className="text-muted-foreground border-b border-border/40">
                    <th className="text-left px-2 py-1">STARTED (ET)</th>
                    <th className="text-left px-2 py-1">SESSION</th>
                    <th className="text-left px-2 py-1">STATUS</th>
                    <th className="text-right px-2 py-1">SYMBOLS</th>
                    <th className="text-right px-2 py-1">RECS</th>
                    <th className="text-right px-2 py-1">ACT</th>
                    <th className="text-right px-2 py-1">AI CALLS</th>
                    <th className="text-right px-2 py-1">COST</th>
                    <th className="text-right px-2 py-1">DURATION</th>
                  </tr>
                </thead>
                <tbody>
                  {scanRuns.runs.map(r => {
                    const isFail = r.status === "FAILED";
                    const isPartial = r.status === "PARTIAL";
                    const isSkipped = r.status === "SKIPPED";
                    const isMissed = r.status === "MISSED";
                    const budget = r.failure_reason === "BUDGET_LIMIT_REACHED";
                    const tone = isFail || budget ? "text-red-500 border-red-500/40"
                                 : isMissed ? "text-orange-500 border-orange-500/40"
                                 : isSkipped ? "text-signal-yellow border-signal-yellow/40"
                                 : isPartial ? "text-[#0A84FF] border-[#0A84FF]/40"
                                 : "text-signal-green border-signal-green/40";
                    const status = isSkipped ? "SKIPPED"
                                   : isMissed ? "MISSED"
                                   : budget ? "BUDGET_LIMIT_REACHED"
                                   : (r.status === "SUCCESS" ? "PASS" : r.status);
                    const start = r.started_at ? new Date(r.started_at) : null;
                    const end = r.completed_at ? new Date(r.completed_at) : null;
                    const durMs = start && end ? end - start : null;
                    const dur = durMs != null ? `${Math.round(durMs/1000)}s` : "—";
                    const et = start ? start.toLocaleString("en-US", {
                      timeZone: "America/New_York", month: "short", day: "numeric",
                      hour: "2-digit", minute: "2-digit", hour12: true
                    }) + " ET" : "—";
                    const cost = r.estimated_cost_usd != null ? `$${Number(r.estimated_cost_usd).toFixed(2)}` : "—";
                    const symbolsCell = r.symbols_requested != null
                      ? `${r.symbols_successful ?? 0} / ${r.symbols_requested}` : "—";
                    const isExpanded = expandedRun === r.scan_run_id;
                    return (
                      <>
                        <tr key={r.scan_run_id} onClick={() => setExpandedRun(isExpanded ? null : r.scan_run_id)}
                            className={`border-b border-border/30 last:border-0 cursor-pointer hover:bg-white/[0.02] ${isFail || budget ? "bg-signal-yellow/[0.03]" : ""}`}
                            data-testid={`scan-run-row-${r.scan_run_id}`}>
                          <td className="px-2 py-1 text-muted-foreground">{et}</td>
                          <td className="px-2 py-1"><span className="chip !text-[9px]">{r.market_session || "—"}</span></td>
                          <td className="px-2 py-1"><span className={`chip !text-[9px] ${tone}`}>{status}</span></td>
                          <td className="text-right px-2 py-1">{symbolsCell}</td>
                          <td className="text-right px-2 py-1">{r.recommendations_generated ?? "—"}</td>
                          <td className="text-right px-2 py-1">{r.actionable_recommendations ?? "—"}</td>
                          <td className="text-right px-2 py-1">{r.analyst_calls ?? "—"}</td>
                          <td className="text-right px-2 py-1">{cost}</td>
                          <td className="text-right px-2 py-1">{dur}</td>
                        </tr>
                        {isExpanded && (
                          <tr key={r.scan_run_id + "-detail"} className="bg-white/[0.02] border-b border-border/30">
                            <td colSpan={9} className="px-4 py-3">
                              <PanelErrorBoundary
                                name={`scan-run-${r.scan_run_id}`}
                                onRetry={() => setExpandedRun(null)}>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-[10px] mono">
                                <div><span className="text-muted-foreground">scan_run_id:</span> {r.scan_run_id}</div>
                                <div><span className="text-muted-foreground">market_session:</span> {r.market_session}</div>
                                <div><span className="text-muted-foreground">scanner_profile:</span> {r.scanner_profile || "—"}</div>
                                <div><span className="text-muted-foreground">started_at (UTC):</span> {r.started_at}</div>
                                <div><span className="text-muted-foreground">completed_at (UTC):</span> {r.completed_at || "—"}</div>
                                <div><span className="text-muted-foreground">duration:</span> {dur}</div>
                                <div><span className="text-muted-foreground">symbols_requested:</span> {r.symbols_requested ?? "—"}</div>
                                <div><span className="text-muted-foreground">symbols_scanned:</span> {r.symbols_scanned ?? "—"}</div>
                                <div><span className="text-muted-foreground">symbols_successful:</span> {r.symbols_successful ?? "—"}</div>
                                <div><span className="text-muted-foreground">symbols_failed:</span> {r.symbols_failed ?? 0}</div>
                                <div>
                                  <span className="text-muted-foreground">symbols_skipped:</span> {r.symbols_skipped ?? 0}
                                  {(r.symbols_skipped ?? 0) > 0 && (r.symbols_failed ?? 0) === 0 && (
                                    <span className="ml-1 text-muted-foreground">(not sent to AI after prescreen/budget selection)</span>
                                  )}
                                </div>
                                <div><span className="text-muted-foreground">recommendations_generated:</span> {r.recommendations_generated ?? "—"}</div>
                                <div><span className="text-muted-foreground">analyst_calls:</span> {r.analyst_calls ?? "—"}</div>
                                <div><span className="text-muted-foreground">critic_calls:</span> {r.critic_calls ?? 0}</div>
                                <div><span className="text-muted-foreground">tokens:</span> {renderScalar(r.tokens)}</div>
                                <div><span className="text-muted-foreground">estimated_cost_usd:</span> ${Number(r.estimated_cost_usd || 0).toFixed(4)}</div>
                                <div><span className="text-muted-foreground">actual_cost_usd:</span> {r.actual_cost_usd != null ? `$${Number(r.actual_cost_usd).toFixed(4)}` : "—"}</div>
                                <div><span className="text-muted-foreground">failure_reason:</span> {r.failure_reason || "—"}</div>
                                <div><span className="text-muted-foreground">retryable:</span> {String(r.retryable ?? false)}</div>
                                <div><span className="text-muted-foreground">snapshots_stamped:</span> {r.snapshots_stamped ?? "—"}</div>
                              </div>
                              {r.budget_snapshot && (
                                <div className="mt-2 text-[10px] mono text-muted-foreground">
                                  budget_snapshot: spent_daily=${Number(r.budget_snapshot.spent_daily || 0).toFixed(4)} · spent_monthly=${Number(r.budget_snapshot.spent_monthly || 0).toFixed(4)} · ok={String(r.budget_snapshot.ok)}
                                </div>
                              )}
                              {r.status === "SKIPPED" && (
                                <div className="mt-2 p-2 border border-signal-yellow/30 bg-signal-yellow/[0.03] text-[10px] mono"
                                      data-testid={`scan-skip-detail-${r.scan_run_id}`}>
                                  <div className="text-signal-yellow uppercase tracking-widest text-[9px] mb-1">
                                    SKIPPED — {(r.skip_reason || "UNKNOWN_PREREQUISITE").replace(/_/g, " ")}
                                  </div>
                                  <div className="text-muted-foreground">
                                    {r.diagnostic || "Scheduled research required a prerequisite that was missing."}
                                  </div>
                                  <div className="text-muted-foreground mt-1">
                                    Zero LLM calls made · Zero cost incurred.
                                  </div>
                                </div>
                              )}
                              {r.diagnostic && (
                                <div className="mt-2 text-[10px] mono text-signal-yellow">
                                  diagnostic: {r.diagnostic}
                                </div>
                              )}
                              <PrescreenTelemetry telemetry={r.prescreen_telemetry} policy={{
                                policy_version: r.scanner_policy_version,
                                policy_id: r.scanner_policy_id,
                                universe_version: r.universe_version,
                              }} scanRunId={r.scan_run_id}/>
                              {r.market_session === "PREMARKET" && (
                                <button
                                  className="mt-3 chip !text-[9px] hover:border-foreground"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const d = (r.started_at || "").slice(0,10);
                                    setAmPmDate(d);
                                    try {
                                      const resp = await api.get(`/ai-quality/morning-vs-midday?date=${d}`);
                                      setAmPm(resp.data);
                                    } catch (err) { setAmPm({ error: "Failed to load comparison" }); }
                                  }}
                                  data-testid={`compare-ampm-${r.scan_run_id}`}
                                >Compare AM vs Midday</button>
                              )}
                              </PanelErrorBoundary>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {scanRuns?.failed > 0 && (
            <div className="mt-3 text-[10px] text-signal-yellow">
              {scanRuns.failed} failed run{scanRuns.failed === 1 ? "" : "s"} in history — research-system health only; broker/execution unaffected.
            </div>
          )}

          {amPm && amPmDate && (
            <div className="mt-6 border-t border-border/40 pt-4">
              <div className="label-cap mb-2">AM vs Midday — {amPmDate}</div>
              {amPm.error ? (
                <div className="text-[11px] pnl-down">{amPm.error}</div>
              ) : (
                <>
                  <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mb-3">
                    {["UNCHANGED","STRENGTHENED","WEAKENED","FLIPPED","CHANGED","NEW_MIDDAY","DROPPED_MIDDAY"].map(k => (
                      <div key={k} className="grid-cell !p-2 text-center">
                        <div className="label-cap !text-[9px]">{k.replace(/_/g," ")}</div>
                        <div className="text-sm mono">{amPm.classification_counts?.[k] ?? 0}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] mono text-muted-foreground mb-2">
                    Morning: {amPm.morning_count} · Midday: {amPm.midday_count} · Overlap: {amPm.overlap_count}
                  </div>
                  {amPm.rows?.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px] mono">
                        <thead>
                          <tr className="text-muted-foreground border-b border-border/40">
                            <th className="text-left px-2 py-1">SYMBOL</th>
                            <th className="text-left px-2 py-1">AM</th>
                            <th className="text-left px-2 py-1">MIDDAY</th>
                            <th className="text-right px-2 py-1">ΔCONF</th>
                            <th className="text-right px-2 py-1">ΔRISK</th>
                            <th className="text-right px-2 py-1">ΔENTRY</th>
                            <th className="text-left px-2 py-1">CLASS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {amPm.rows.slice(0, 40).map(r => (
                            <tr key={r.symbol + r.classification} className="border-b border-border/30 last:border-0">
                              <td className="px-2 py-1 font-medium">{r.symbol}</td>
                              <td className="px-2 py-1 text-muted-foreground">{r.morning_verdict || "—"}</td>
                              <td className="px-2 py-1">{r.midday_verdict || "—"}</td>
                              <td className="text-right px-2 py-1">{r.confidence_change ?? "—"}</td>
                              <td className="text-right px-2 py-1">{r.risk_score_change ?? "—"}</td>
                              <td className="text-right px-2 py-1">{r.entry_change ?? "—"}</td>
                              <td className="px-2 py-1"><span className="chip !text-[9px]">{r.classification.replace(/_/g," ")}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <div className="mt-3 text-[10px] text-muted-foreground">
            Read-only observation surface. Configuration + control actions intentionally absent — experiment freeze in effect.
          </div>
        </section>
      )}

      {tab === "autonomy" && (
        <section className="grid-cell !p-3" data-testid="autonomy-panel">
          <div className="flex items-center gap-2 mb-3">
            <LockKeyhole size={14} className="text-signal-yellow"/>
            <span className="label-cap">Autonomy Readiness Gate</span>
            <span className={`chip !text-[9px] ${autonomy?.all_pass ? "text-signal-green border-signal-green/40" : "text-signal-yellow border-signal-yellow/40"}`}>
              {autonomy?.status?.replace(/_/g," ") || "NOT READY"}
            </span>
            <span className="chip !text-[9px] text-signal-yellow border-signal-yellow/40 ml-auto">
              AUTONOMOUS TRADING LOCKED
            </span>
          </div>
          <table className="w-full text-[11px] mono">
            <thead>
              <tr className="text-muted-foreground border-b border-border/40">
                <th className="text-left px-2 py-1">GATE</th>
                <th className="text-left px-2 py-1">VALUE</th>
                <th className="text-left px-2 py-1">THRESHOLD</th>
                <th className="text-left px-2 py-1">STATE</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(autonomy?.gates || {}).map(([k, g]) => (
                <tr key={k} className="border-b border-border/30 last:border-0">
                  <td className="px-2 py-1">{k.replace(/_/g," ")}</td>
                  <td className="px-2 py-1">{g.value ?? "—"}</td>
                  <td className="px-2 py-1 text-muted-foreground">{autonomy?.thresholds?.[`min_${k}`] ?? autonomy?.thresholds?.[`max_${k}`] ?? "—"}</td>
                  <td className={`px-2 py-1 ${g.ok ? "text-signal-green" : g.state === "WAITING" ? "text-signal-yellow" : "pnl-down"}`}>
                    {g.state}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 text-[11px] text-muted-foreground">
            Even if every gate passes, autonomous trading remains LOCKED. Enabling requires explicit owner approval (backend flag not exposed by UI toggle).
          </div>
        </section>
      )}
    </div>
  );
}


// ─── PHASE C1 · Scan Runs Prescreen Telemetry ─────────────────────
// Exposes the persisted funnel (universe → tradable → price → liquidity
// → affordability → sent-to-AI → recommendations) plus rejection counts
// so the owner can answer "why wasn't MSFT analyzed?" without reading
// source code. Zero LLM, zero mutation — pure read from scan_run_log.

const REJECTION_LABELS = {
  price_above_max: "PRICE_TOO_HIGH",
  price_below_min: "PRICE_TOO_LOW",
  insufficient_avg_dollar_volume: "INSUFFICIENT_ADV",
  insufficient_daily_volume: "INSUFFICIENT_VOLUME",
  insufficient_whole_share_affordability: "INSUFFICIENT_AFFORDABILITY",
  leveraged_etf: "LEVERAGED_ETF",
  inverse_etf: "INVERSE_ETF",
  non_tradable: "NON_TRADABLE",
  no_market_data: "DATA_MISSING",
};

function PrescreenTelemetry({ telemetry, policy, scanRunId }) {
  if (!telemetry || Object.keys(telemetry).length === 0) {
    return (
      <div className="mt-3 text-[10px] mono text-muted-foreground border-t border-border/30 pt-2"
            data-testid={`prescreen-empty-${scanRunId}`}>
        No prescreen telemetry recorded for this run (predates PHASE A capital-aware scanner).
      </div>
    );
  }
  const universe = telemetry.universe_size ?? 0;
  const quoted = telemetry.quoted ?? universe;
  const afterTrad = telemetry.after_tradability ?? quoted;
  const afterPrice = telemetry.after_price ?? afterTrad;
  const afterLiq = telemetry.after_liquidity ?? afterPrice;
  const afterAff = telemetry.after_affordability ?? afterLiq;
  const sent = telemetry.sent_to_ai ?? 0;
  const recs = telemetry.recommendations_persisted ?? 0;
  const rejections = telemetry.rejections_by_reason || {};

  const steps = [
    { label: "Universe", value: universe, tone: "text-foreground" },
    { label: "Quoted", value: quoted, tone: "text-foreground" },
    { label: "Tradable", value: afterTrad, tone: "text-foreground" },
    { label: "Price Eligible", value: afterPrice, tone: "text-foreground" },
    { label: "Liquidity Eligible", value: afterLiq, tone: "text-foreground" },
    { label: "Affordability Eligible", value: afterAff, tone: "text-foreground" },
    { label: "Sent to Analyst", value: sent, tone: "text-[#0A84FF]" },
    { label: "Recommendations", value: recs, tone: "text-signal-green" },
  ];
  const maxVal = Math.max(1, ...steps.map((s) => s.value));

  return (
    <div className="mt-3 border-t border-border/30 pt-3"
          data-testid={`prescreen-telemetry-${scanRunId}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="label-cap">Prescreen Funnel · read-only</div>
        <div className="text-[9px] mono text-muted-foreground">
          policy {policy.policy_version ?? "—"}
          {policy.policy_id ? ` · id ${String(policy.policy_id).slice(0, 8)}` : ""}
          {policy.universe_version ? ` · universe ${policy.universe_version}` : ""}
        </div>
      </div>
      <div className="space-y-1">
        {steps.map((s) => {
          const pct = Math.round((s.value / maxVal) * 100);
          return (
            <div key={s.label} className="grid grid-cols-[160px_1fr_60px] items-center gap-2 text-[10px] mono"
                  data-testid={`funnel-step-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="text-muted-foreground uppercase tracking-widest">{s.label}</div>
              <div className="h-3 bg-white/[0.03] border border-border/20 relative overflow-hidden">
                <div className={`h-full ${s.tone.replace("text-", "bg-")} opacity-40`}
                      style={{ width: `${pct}%` }}/>
              </div>
              <div className={`text-right ${s.tone}`}>{s.value.toLocaleString()}</div>
            </div>
          );
        })}
      </div>
      {Object.keys(rejections).length > 0 && (
        <div className="mt-3">
          <div className="label-cap mb-1">Rejections by Reason</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(rejections)
              .sort(([, a], [, b]) => b - a)
              .map(([reason, count]) => (
                <span key={reason}
                      className="chip !text-[9px] text-signal-yellow border-signal-yellow/30"
                      data-testid={`rejection-${reason}`}>
                  {(REJECTION_LABELS[reason] || reason).toUpperCase()}: {count}
                </span>
              ))}
          </div>
        </div>
      )}
      <div className="mt-2 text-[9px] text-muted-foreground">
        Discovery eligibility ≠ trade eligibility. A stock that passes
        every gate here may still be rejected by broker risk rules,
        sector caps, or the analyst LLM downstream.
      </div>
    </div>
  );
}
